import type {
  BlueprintDocument,
  ChatEvent,
  PlaybackFact,
  QuickFacts,
  ScopingCallContact,
  Session,
} from "./types";

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { "Content-Type": "application/json", ...init?.headers },
    ...init,
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`API error ${res.status}: ${detail}`);
  }
  return res.json() as Promise<T>;
}

export async function createSession(): Promise<{ session_id: string; stage: number }> {
  return apiFetch("/sessions", { method: "POST" });
}

export async function getSession(sessionId: string): Promise<Session> {
  return apiFetch(`/sessions/${sessionId}`);
}

export async function submitOnramp(sessionId: string, facts: Partial<QuickFacts>): Promise<void> {
  await apiFetch(`/sessions/${sessionId}/onramp`, {
    method: "POST",
    body: JSON.stringify(facts),
  });
}

export async function getPlayback(sessionId: string): Promise<PlaybackFact[]> {
  return apiFetch(`/sessions/${sessionId}/playback`);
}

export async function editPlaybackFact(
  sessionId: string,
  factId: string,
  value: string,
): Promise<PlaybackFact> {
  return apiFetch(`/sessions/${sessionId}/playback/${factId}`, {
    method: "PATCH",
    body: JSON.stringify({ value }),
  });
}

export async function confirmPlayback(sessionId: string): Promise<void> {
  await apiFetch(`/sessions/${sessionId}/playback/confirm`, { method: "POST" });
}

export async function generateBlueprint(sessionId: string): Promise<BlueprintDocument> {
  return apiFetch(`/sessions/${sessionId}/blueprint`, { method: "POST" });
}

export async function deleteSession(sessionId: string): Promise<void> {
  try {
    const res = await fetch(`/sessions/${sessionId}`, { method: "DELETE" });
    // 204 = deleted, 404 = already gone — both are fine
    if (!res.ok && res.status !== 404) {
      console.warn(`deleteSession: unexpected status ${res.status}`);
    }
  } catch {
    // Network error — ignore, local state will still be cleared
  }
}

export function downloadBlueprintPdf(sessionId: string): void {
  window.location.href = `/sessions/${sessionId}/blueprint/pdf`;
}

export async function bookScopingCall(
  sessionId: string,
  contact: ScopingCallContact,
): Promise<void> {
  await apiFetch(`/sessions/${sessionId}/scoping-call`, {
    method: "POST",
    body: JSON.stringify(contact),
  });
}

/**
 * Opens an SSE stream to /sessions/{id}/chat.
 * Calls onEvent for each parsed ChatEvent.
 * Returns a cleanup function that closes the underlying XHR/fetch stream.
 */
export function createChatStream(
  sessionId: string,
  message: string,
  chipsSelected: string[],
  onEvent: (event: ChatEvent) => void,
  onDone: () => void,
  onError: (err: string) => void,
): () => void {
  let cancelled = false;

  (async () => {
    let res: Response;
    try {
      res = await fetch(`/sessions/${sessionId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, chips_selected: chipsSelected }),
      });
    } catch (e) {
      if (!cancelled) onError(String(e));
      return;
    }

    if (!res.ok || !res.body) {
      if (!cancelled) onError(`Chat request failed: ${res.status}`);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done || cancelled) break;
      buffer += decoder.decode(value, { stream: true });

      // Parse SSE lines
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const raw = line.slice(6).trim();
          try {
            const parsed = JSON.parse(raw) as ChatEvent;
            if (!cancelled) onEvent(parsed);
          } catch {
            // malformed data line — skip
          }
        }
      }
    }

    if (!cancelled) onDone();
  })();

  return () => {
    cancelled = true;
  };
}
